// src/pages/SsoCallback.js
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { getUserMessage } from "../api/axios";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/useAuth";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const SsoCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { login } = useAuth();

  const [status, setStatus] = useState("processing"); // processing | success | error
  const [errorMessage, setErrorMessage] = useState("");

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  useEffect(() => {
    const token = query.get("token");
    const labId = query.get("lab_id");
    const next = query.get("next");

    // fallback: éviter "/" (home souvent en mode invité) → dashboard
    const fallbackNext = labId ? `/reservations?lab_id=${labId}` : "/dashboard";
    const target = next && next.startsWith("/") ? next : fallbackNext;

    if (!token) {
      setStatus("error");
      setErrorMessage(t("sso.missingToken", "Token SSO manquant dans l’URL."));
      return;
    }

    const waitForSession = async () => {
      // Retry court : user peut être créé côté labs au 1er passage
      const delays = [0, 250, 500, 800, 1200]; // ~2.75s
      let lastErr = null;

      for (let i = 0; i < delays.length; i++) {
        if (delays[i] > 0) await sleep(delays[i]);
        try {
          // ✅ ton endpoint existant
          await api.get("/auth/me", { skipUnauthorizedRedirect: true });
          return true;
        } catch (e) {
          lastErr = e;
        }
      }
      throw lastErr || new Error("Session not ready");
    };

    const run = async () => {
      try {
        setStatus("processing");
        setErrorMessage("");

        // 1) Échange token WP -> token Labs
        const resp = await api.get("/auth/sso/wordpress", {
          params: { token, lab_id: labId || undefined },
          skipUnauthorizedRedirect: true,
        });

        const data = resp?.data || {};
        const accessToken = data.access_token || data.token || null;
        const user = data.user || null;
        const reservationId = data.reservation_id || null;

        if (!accessToken) {
          throw new Error("Token d’accès Labs manquant dans la réponse backend.");
        }

        // 2) Stockage token + header axios
        try {
          localStorage.setItem("access_token", accessToken);
        } catch {}

        api.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;

        // ✅ FIX CRITIQUE : synchro immédiate du state AuthProvider (supprime le double-clic)
        // (ton App/RequireAuth se base sur ce state, pas sur localStorage directement)
        try {
          login(accessToken);
        } catch {
          // si jamais login échoue, on garde au moins localStorage
        }

        if (user) {
          try {
            localStorage.setItem("current_user", JSON.stringify(user));
          } catch {}
        }

        // 3) Attendre que /auth/me passe avant redirection
        await waitForSession();

        setStatus("success");
        setStatus("success");

        // 3bis) Reservation-aware SSO (P0-03-FUNCTIONAL) : le backend a
        // deja valide (existence, ownership, statut) le reservation_id
        // transmis explicitement par WordPress -- naviguer directement
        // vers cette reservation precise, SANS passer par
        // POST /labs/{labId}/launch (qui creerait/reutiliserait une
        // reservation potentiellement differente de celle selectionnee).
        if (reservationId) {
          navigate("/lab/" + reservationId, { replace: true });
          return;
        }

        // 4) Auto-lancer le lab si lab_id present (flow legacy, inchange :
        // reservation_id absent de la reponse backend)
        if (labId) {
          try {
            let duration_minutes = 60;
            try {
              const walletResp = await api.get("/reservations/lab-credits/me");
              const wallets = walletResp?.data?.summary?.wallets || walletResp?.data?.wallets || [];
              const activeWallet = wallets.find(w => w.is_active && w.remaining_minutes > 0);
              if (activeWallet) {
                duration_minutes = Math.min(activeWallet.remaining_minutes, 120);
                duration_minutes = Math.max(duration_minutes, 10);
              }
            } catch (we) { console.warn("[SSO] Wallet fetch failed", we); }
            const launchResp = await api.post("/labs/" + labId + "/launch", { mode: "hybrid", duration_minutes });
            const launchData = launchResp?.data || {};
            if (launchData.status === "immediate" && launchData.reservation_id) {
              navigate("/lab/" + launchData.reservation_id, { replace: true });
              return;
            } else if (launchData.status === "already_running" && launchData.reservation_id) {
              navigate("/lab/" + launchData.reservation_id, { replace: true });
              return;
            } else if (launchData.status === "reservation_required") {
              navigate(
                "/reservations?message=" +
                encodeURIComponent(
                  launchData.message || "Veuillez choisir un cr\u00e9neau."
                )
              );
              return;
            }
          } catch (le) { console.warn("[SSO] Launch failed", le?.response?.data); }
        }

        // 5) Redirection finale
        navigate(target, { replace: true });
      } catch (error) {
        console.error("Erreur SSO callback:", error);
        const detail = error?.response?.data?.detail;
        let msg;
        if (detail?.message) {
          // Message structuré backend : SSO_TOKEN_EXPIRED, SSO_USER_NOT_FOUND, etc.
          msg = detail.message;
        } else if (detail && typeof detail === "string") {
          msg = detail;
        } else if (!error?.response) {
          // Pas de réponse = backend injoignable ou proxy CRA non démarré
          msg = t('sso.server_unreachable');
        } else {
          msg = getUserMessage(error, "Impossible de finaliser l'authentification SSO.");
        }
        setErrorMessage(msg);
        setStatus("error");
      }
    };

    run();
  }, [query, navigate, t, login]);

  /*

  if (status === "processing") {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <h2>{t("sso.loadingTitle", t('sso.connecting'))}</h2>
        <p>{t("sso.loadingText", t('sso.preparing'))}</p>
      </div>
    );
  }
*/
  if (status === "processing") {
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.logoDot} />
          <h2 style={styles.title}>{t('sso.connecting')}</h2>
        </div>

        <p style={styles.subtitle}>
          Nous préparons votre accès aux labs NextHop. Patiente une seconde.
        </p>

        {/* Progress bar indéterminée */}
        <div style={styles.progressTrack} aria-label="Chargement">
          <div style={styles.progressBar} />
        </div>

        <div style={styles.hints}>
          <span style={styles.hintPill}>SSO</span>
          <span style={styles.hintPill}>{t('sso.sync')}</span>
          <span style={styles.hintPill}>{t('sso.redirect')}</span>
        </div>
      </div>

      {/* CSS inline via <style> pour animation */}
      <style>{`
        @keyframes nh-slide {
          0% { transform: translateX(-60%); }
          100% { transform: translateX(220%); }
        }
      `}</style>
    </div>
  );
}


  if (status === "error") {
    return (
      <div style={{ padding: "2rem", textAlign: "center", color: "red" }}>
        <h2>{t("sso.errorTitle", "Erreur SSO")}</h2>
        <p>{errorMessage}</p>
        <button onClick={() => navigate("/login", { replace: true })}>
          {t("sso.backLogin", t('sso.go_login'))}
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: "2rem", textAlign: "center" }}>
      <h2>{t("sso.successTitle", t('sso.success'))}</h2>
      <p>{t("sso.successText", t('sso.redirecting'))}</p>
    </div>
  );
};

const styles = {
  page: {
    minHeight: "70vh",
    display: "grid",
    placeItems: "center",
    padding: "24px",
  },
  card: {
    width: "min(520px, 92vw)",
    borderRadius: "16px",
    padding: "22px 20px",
    boxShadow: "0 10px 30px rgba(0,0,0,.08)",
    border: "1px solid rgba(0,0,0,.06)",
    background: "#fff",
  },
  header: { display: "flex", alignItems: "center", gap: "10px" },
  logoDot: {
    width: 12,
    height: 12,
    borderRadius: 999,
    background: "linear-gradient(135deg,#111,#555)",
  },
  title: { margin: 0, fontSize: 18 },
  subtitle: { margin: "10px 0 14px", color: "rgba(0,0,0,.70)", lineHeight: 1.4 },

  progressTrack: {
    position: "relative",
    height: 10,
    borderRadius: 999,
    overflow: "hidden",
    background: "rgba(0,0,0,.06)",
  },
  progressBar: {
    position: "absolute",
    inset: 0,
    width: "40%",
    borderRadius: 999,
    background: "linear-gradient(90deg, rgba(0,0,0,.08), rgba(0,0,0,.35), rgba(0,0,0,.08))",
    animation: "nh-slide 1.15s ease-in-out infinite",
  },

  hints: { display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" },
  hintPill: {
    fontSize: 12,
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(0,0,0,.05)",
    color: "rgba(0,0,0,.70)",
  },
};


export default SsoCallback;
